import { useNavigate } from 'react-router-dom';
import type { Account } from '../../../hooks/useAccounts';
import { formatCurrency } from '../../../../lib/formatters';

const TYPE_LABELS: Record<string, string> = {
  corrente: 'Conta Corrente', digital: 'Conta Digital', poupanca: 'Poupança',
  carteira: 'Carteira', investimento: 'Investimentos', dinheiro: 'Dinheiro em Espécie',
  corretora: 'Corretora', dolar: 'Conta em Dólar', euro: 'Conta em Euro',
  outra_moeda: 'Outra Moeda', cripto: 'Criptoativos', internacional: 'Internacional', pj: 'Conta PJ',
};
const TYPE_ICONS: Record<string, string> = {
  corrente: 'ri-bank-line', digital: 'ri-smartphone-line', poupanca: 'ri-safe-line',
  carteira: 'ri-wallet-3-line', investimento: 'ri-stock-line', dinheiro: 'ri-money-dollar-circle-line',
  corretora: 'ri-line-chart-line', dolar: 'ri-currency-line', euro: 'ri-currency-line',
  outra_moeda: 'ri-exchange-line', cripto: 'ri-bit-coin-line', internacional: 'ri-global-line', pj: 'ri-building-line',
};

interface StatementHeaderProps {
  account: Account | null;
  loading: boolean;
  currentBalance: number | null;
  periodLabel: string;
}

export function StatementHeader({ account, loading, currentBalance, periodLabel }: StatementHeaderProps) {
  const navigate = useNavigate();
  const icon = TYPE_ICONS[account?.type ?? ''] || 'ri-bank-line';
  const label = TYPE_LABELS[account?.type ?? ''] || 'Conta';
  const currency = account?.currency || 'BRL';

  return (
    <div>
      {/* Breadcrumb */}
      <div className="flex items-center gap-1.5 mb-4 flex-wrap">
        <button
          onClick={() => navigate('/contas')}
          className="flex items-center gap-1 text-sm text-gray-400 hover:text-gray-600 cursor-pointer transition-colors"
        >
          <i className="ri-arrow-left-line text-xs" />
          <span>Contas</span>
        </button>
        <i className="ri-arrow-right-s-line text-gray-300 text-xs" />
        {loading ? (
          <div className="w-20 h-4 bg-gray-100 rounded animate-pulse" />
        ) : (
          <button
            onClick={() => navigate('/contas')}
            className="text-sm text-gray-500 hover:text-gray-700 cursor-pointer transition-colors"
          >
            {account?.name}
          </button>
        )}
        <i className="ri-arrow-right-s-line text-gray-300 text-xs" />
        <span className="text-sm font-600 text-gray-800">Extrato</span>
      </div>

      {/* Title row */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        {/* Left: account info */}
        <div className="flex items-center gap-4">
          {loading ? (
            <div className="w-12 h-12 bg-gray-100 rounded-2xl animate-pulse flex-shrink-0" />
          ) : (
            <div
              className="w-12 h-12 rounded-2xl flex items-center justify-center flex-shrink-0"
              style={{ backgroundColor: (account?.color ?? '#10b981') + '20' }}
            >
              <i className={`${icon} text-xl`} style={{ color: account?.color ?? '#10b981' }} />
            </div>
          )}
          <div>
            {loading ? (
              <>
                <div className="w-40 h-6 bg-gray-100 rounded animate-pulse mb-1.5" />
                <div className="w-28 h-4 bg-gray-100 rounded animate-pulse" />
              </>
            ) : (
              <>
                <h1 className="text-xl font-700 text-gray-900">{account?.name}</h1>
                <p className="text-sm text-gray-500 mt-0.5 flex items-center gap-1.5">
                  {account?.bank && <span>{account.bank}</span>}
                  {account?.bank && <span className="text-gray-300">·</span>}
                  <span>{label}</span>
                  {account?.is_archived && (
                    <span className="ml-1 text-xs bg-amber-100 text-amber-600 px-1.5 py-0.5 rounded-full">Arquivada</span>
                  )}
                </p>
              </>
            )}
          </div>
        </div>

        {/* Right: period balance */}
        <div className="bg-white rounded-2xl border border-gray-100 px-5 py-3 text-right">
          <p className="text-xs text-gray-400 mb-0.5">Saldo do período</p>
          {loading ? (
            <div className="w-28 h-7 bg-gray-100 rounded animate-pulse" />
          ) : (
            <p className={`text-2xl font-700 ${(currentBalance ?? 0) >= 0 ? 'text-gray-900' : 'text-red-500'}`}>
              {formatCurrency(currentBalance ?? 0, currency)}
            </p>
          )}
          <p className="text-xs text-gray-400 mt-0.5">{periodLabel}</p>
        </div>
      </div>
    </div>
  );
}
